import { useEffect, useId, useRef } from 'react';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { styles } from '../lib/styles';

// Shared modal wrapper on native <dialog>/showModal(): focus trap, Escape-to-close,
// and focus restore come from the platform. The backdrop dim lives in global.css
// (dialog::backdrop) because inline styles cannot target pseudo-elements.
export function Dialog({ title, titleStyle, onClose, children }: {
  title: ReactNode;
  titleStyle?: CSSProperties;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dlg = ref.current;
    if (dlg && !dlg.open) dlg.showModal();
  }, []);

  const handleClick = (e: MouseEvent<HTMLDialogElement>) => {
    const dlg = ref.current;
    if (!dlg || e.target !== dlg) return;
    // Backdrop clicks target the <dialog> itself with coordinates outside its box.
    const r = dlg.getBoundingClientRect();
    if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) {
      dlg.close();
    }
  };

  return (
    <dialog ref={ref} style={styles.modalContent} aria-labelledby={titleId} onClose={onClose} onClick={handleClick}>
      <h3 id={titleId} style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, ...titleStyle }}>{title}</h3>
      {children}
    </dialog>
  );
}
